import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, Trash2, Edit, RefreshCw, Shield, TrendingUp, DollarSign, Heart } from 'lucide-react';
import AdminReports from './AdminReports';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadDashboard();
  }, [page, searchQuery]);

  const loadDashboard = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      
      // Load stats
      const statsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setStats(statsResponse.data);

      // Load users
      const usersResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          params: { skip: page * limit, limit, search: searchQuery || undefined }
        }
      );
      setUsers(usersResponse.data.users);
      setTotalUsers(usersResponse.data.total);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load admin dashboard:', error);
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/');
      }
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('User deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      loadDashboard();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getTierBadgeColor = (tier) => {
    switch(tier) {
      case 'vip': return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black';
      case 'premium': return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center overflow-x-hidden">
        <div className="text-white text-2xl">Loading Admin Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Shield className="h-10 w-10 text-yellow-400" />
            <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-lg transition"
          >
            Back to App
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Users</p>
                  <p className="text-white text-3xl font-bold">{stats.total_users}</p>
                </div>
                <Users className="h-12 w-12 text-blue-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Premium Users</p>
                  <p className="text-white text-3xl font-bold">{stats.tier_breakdown.premium + stats.tier_breakdown.vip}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-purple-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Matches</p>
                  <p className="text-white text-3xl font-bold">{stats.total_matches}</p>
                </div>
                <Heart className="h-12 w-12 text-rose-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Recent Signups (7d)</p>
                  <p className="text-white text-3xl font-bold">{stats.recent_signups}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-green-400" />
              </div>
            </div>
          </div>
        )}

        {/* Safety Reports */}
        <AdminReports />

        {/* User Management */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">User Management</h2>
            <button
              onClick={() => loadDashboard()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
              <input
                type="text"
                placeholder="Search by username, email, or name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40"
              />
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left text-white/70 py-3 px-4">Username</th>
                  <th className="text-left text-white/70 py-3 px-4">Email</th>
                  <th className="text-left text-white/70 py-3 px-4">Name</th>
                  <th className="text-left text-white/70 py-3 px-4">Tier</th>
                  <th className="text-left text-white/70 py-3 px-4">Matches</th>
                  <th className="text-left text-white/70 py-3 px-4">Status</th>
                  <th className="text-right text-white/70 py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-white/10 hover:bg-white/5 transition">
                    <td className="py-4 px-4 text-white">{user.username}</td>
                    <td className="py-4 px-4 text-white/80">{user.email}</td>
                    <td className="py-4 px-4 text-white/80">{user.name}</td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTierBadgeColor(user.tier)}`}>
                        {user.tier.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-white/80">{user.match_count}</td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs ${user.is_email_confirmed ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                        {user.is_email_confirmed ? 'Confirmed' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteConfirm(true);
                          }}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-300 p-2 rounded-lg transition"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-white/70">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalUsers)} of {totalUsers}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= totalUsers}
                className="bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-md w-full border border-red-500/50">
            <h3 className="text-2xl font-bold text-white mb-4">Delete User?</h3>
            <p className="text-white/80 mb-2">
              Are you sure you want to delete <strong>{selectedUser.username}</strong>?
            </p>
            <p className="text-red-300 mb-6 text-sm">
              This will permanently delete all user data including matches, messages, and subscriptions. This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleDelete(selectedUser.id)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-semibold transition"
              >
                Delete Permanently
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-semibold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
